import React, { useCallback, useEffect, useRef, useState } from "react";
import { Check, Loader2, PenLine } from "lucide-react";

import {
  confirmBrandPortrait,
  generateBrandPortrait,
  getLatestBrandPortrait,
  reviseBrandPortrait,
} from "../../lib/brandStudioApi";

const BLOCKS = [
  ["business", "Твоё дело"],
  ["offering", "Что ты создаёшь"],
  ["audience", "Для кого"],
  ["important", "Что особенно важно"],
  ["differentiation", "Что тебя отличает"],
  ["desired_impression", "Какое впечатление должен оставлять бренд"],
  ["preserve", "Что важно сохранить"],
  ["avoid", "Что точно не подходит"],
];

const CLARIFICATIONS = [
  ["offering", "Что именно ты создаёшь или предлагаешь?"],
  ["audience", "Для кого ты в первую очередь работаешь?"],
  ["important", "Что для тебя особенно важно в работе?"],
  ["differentiation", "Что в твоём подходе отличает тебя от похожих проектов?"],
  ["desired_impression", "Какое чувство должен оставлять бренд у человека?"],
  ["preserve", "Есть ли что-то, что обязательно нужно сохранить?"],
  ["avoid", "Что точно не должно появиться в бренде?"],
];

export default function BrandUnderstandingStepV2({ story, onContinue = null }) {
  const [status, setStatus] = useState("loading");
  const [portrait, setPortrait] = useState(null);
  const [editing, setEditing] = useState(false);
  const [content, setContent] = useState({});
  const [clarifications, setClarifications] = useState({});
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const lock = useRef(false);

  const load = useCallback(async () => {
    if (!story) return;
    setStatus("loading");
    setError("");
    try {
      const latest = await getLatestBrandPortrait();
      const matchesStory = Boolean(
        latest &&
          String(latest.source_story_id || "") === String(story?.id || "") &&
          Number(latest.source_story_version || 0) === Number(story?.source_version || 0),
      );
      setPortrait(matchesStory ? latest : null);
      setStatus(matchesStory ? "ready" : "empty");
    } catch {
      setStatus("error");
    }
  }, [story?.id]);

  useEffect(() => {
    load();
  }, [load]);

  const run = async (action) => {
    if (lock.current) return;
    lock.current = true;
    setBusy(action);
    setError("");
    try {
      const result =
        action === "generate"
          ? await generateBrandPortrait()
          : action === "save"
            ? await reviseBrandPortrait(portrait.id, content)
            : await confirmBrandPortrait(portrait.id);
      setPortrait(result);
      setEditing(false);
      setClarifications({});
      setStatus("ready");
    } catch (problem) {
      const conflict = problem?.response?.status === 409;
      setError(
        conflict
          ? "Этот этап уже изменился. Обнови сохранённую версию и попробуй ещё раз."
          : action === "generate"
            ? "Не удалось собрать понимание бренда. Твой рассказ сохранён."
            : action === "save"
              ? "Не удалось сохранить правки. Они остались на экране."
              : "Не удалось подтвердить этот этап. Сохранённая версия на месте.",
      );
      if (action === "generate") setStatus("empty");
    } finally {
      setBusy("");
      lock.current = false;
    }
  };

  const beginEdit = () => {
    setContent({ ...portrait.content });
    setEditing(true);
    setError("");
  };

  const missingClarifications = portrait
    ? CLARIFICATIONS.filter(([key]) => !portrait.content[key]?.trim()).slice(0, 3)
    : [];

  const clarificationValues = Object.values(clarifications).filter(
    (value) => value?.trim(),
  );

  const saveClarifications = async () => {
    if (!portrait || !clarificationValues.length || lock.current) return;
    lock.current = true;
    setBusy("clarify");
    setError("");
    try {
      const nextContent = {
        ...portrait.content,
        ...Object.fromEntries(
          Object.entries(clarifications)
            .filter(([, value]) => value?.trim())
            .map(([key, value]) => [key, value.trim()]),
        ),
      };
      const result = await reviseBrandPortrait(portrait.id, nextContent);
      setPortrait(result);
      setClarifications({});
      setStatus("ready");
    } catch (problem) {
      const conflict = problem?.response?.status === 409;
      setError(
        conflict
          ? "Этот этап уже изменился. Обнови сохранённую версию и попробуй ещё раз."
          : "Не удалось сохранить уточнения. Они остались на экране.",
      );
    } finally {
      setBusy("");
      lock.current = false;
    }
  };

  if (!story) return null;

  return (
    <section
      className="mt-6 rounded-[22px] border border-[#E1D4C2] bg-[#FFFDF9] p-5 md:p-7"
      data-testid="brand-understanding-v2"
    >
      <div className="text-[9.5px] uppercase tracking-[0.22em] text-[#8A6D53]">
        ПОНИМАНИЕ
      </div>

      {status === "loading" && (
        <div className="mt-5 inline-flex items-center gap-2 text-sm text-[#796652]">
          <Loader2 size={16} className="animate-spin" /> Открываю следующий этап…
        </div>
      )}

      {status === "error" && (
        <div className="mt-5">
          <h2 className="font-serif text-[28px] text-[#2B211A]">
            Не удалось открыть этот этап
          </h2>
          <p className="mt-2 text-[13px] leading-5 text-[#6B5540]">
            Твой подтверждённый рассказ на месте.
          </p>
          <button
            type="button"
            onClick={load}
            className="mt-4 min-h-11 rounded-full border border-[#D2C1AA] bg-white px-5 text-sm text-[#4A3728]"
          >
            Попробовать снова
          </button>
        </div>
      )}

      {status === "empty" && (
        <div className="mt-4 max-w-2xl">
          <h2 className="font-serif text-[31px] leading-none text-[#2B211A] md:text-[36px]">
            Теперь я соберу главное
          </h2>
          <p className="mt-3 text-[13.5px] leading-6 text-[#655141]">
            Я разберу твой рассказ и покажу, как поняла твоё дело, аудиторию,
            характер и важные ограничения. Ты сможешь поправить любую деталь.
          </p>
          <button
            type="button"
            disabled={Boolean(busy)}
            onClick={() => run("generate")}
            className="mt-5 min-h-12 rounded-full bg-[#3D2A20] px-6 text-sm text-[#FFF8ED] disabled:opacity-50"
            data-testid="brand-understanding-generate"
          >
            {busy === "generate" ? "Собираю…" : "Показать, как ты меня поняла"}
          </button>
          {busy === "generate" && (
            <div className="mt-4 text-[12.5px] leading-5 text-[#806B55]" role="status">
              Изучаю рассказ · выделяю главное · собираю характер бренда
            </div>
          )}
        </div>
      )}

      {status === "ready" && portrait && (
        <div className="mt-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h2 className="font-serif text-[32px] leading-none text-[#2B211A] md:text-[38px]">
                Я поняла тебя так
              </h2>
              <p className="mt-3 max-w-2xl text-[13.5px] leading-6 text-[#655141]">
                Проверь, всё ли правильно. Здесь можно изменить любую формулировку.
              </p>
            </div>
            {portrait.status === "confirmed" && !editing && (
              <div className="inline-flex shrink-0 items-center gap-2 rounded-full bg-[#EEF0DF] px-3 py-1.5 text-[12px] text-[#586044]">
                <Check size={14} /> Подтверждено
              </div>
            )}
          </div>

          <div className="mt-5 grid gap-3 md:grid-cols-2">
            {BLOCKS.filter(([key]) => editing || portrait.content[key]?.trim()).map(
              ([key, label]) => (
                <article
                  key={key}
                  className="min-w-0 rounded-[14px] border border-[#E4D7C6] bg-[#FAF6EF] p-4"
                >
                  <div className="text-[10px] uppercase tracking-[0.16em] text-[#927A5B]">
                    {label}
                  </div>
                  {editing ? (
                    <textarea
                      value={content[key] || ""}
                      maxLength={5000}
                      onChange={(event) =>
                        setContent((previous) => ({
                          ...previous,
                          [key]: event.target.value,
                        }))
                      }
                      rows={4}
                      className="mt-3 min-h-28 w-full resize-y rounded-xl border border-[#D8C7AE] bg-white p-3 text-[14px] leading-6 text-[#3E2E20] outline-none focus:border-[#8C6C49]"
                    />
                  ) : (
                    <p className="mt-2 whitespace-pre-wrap break-words text-[14px] leading-6 text-[#4C392B]">
                      {portrait.content[key]}
                    </p>
                  )}
                </article>
              ),
            )}
          </div>

          {portrait.status !== "confirmed" && !editing && missingClarifications.length > 0 && (
            <div
              className="mt-5 rounded-[14px] border border-[#E4D7C6] bg-white p-4 md:p-5"
              data-testid="brand-clarifications-v2"
            >
              <div className="text-[9.5px] uppercase tracking-[0.18em] text-[#927A5B]">
                КОРОТКИЕ УТОЧНЕНИЯ
              </div>
              <h3 className="mt-2 font-serif text-[25px] leading-tight text-[#2B211A]">
                Пара вещей, которых не было в рассказе
              </h3>
              <p className="mt-2 max-w-2xl text-[12.5px] leading-5 text-[#806B55]">
                Отвечай только на то, что важно. Остальное можно оставить на потом.
              </p>
              <div className="mt-4 grid gap-3">
                {missingClarifications.map(([key, question]) => (
                  <label key={key} className="block">
                    <span className="text-[12.5px] leading-5 text-[#4C392B]">
                      {question}
                    </span>
                    <textarea
                      value={clarifications[key] || ""}
                      onChange={(event) =>
                        setClarifications((previous) => ({
                          ...previous,
                          [key]: event.target.value,
                        }))
                      }
                      rows={2}
                      maxLength={5000}
                      className="mt-1.5 w-full resize-y rounded-xl border border-[#D8C7AE] bg-[#FFFDF9] p-3 text-[13.5px] leading-6 text-[#3E2E20] outline-none focus:border-[#8C6C49]"
                      data-testid={"brand-clarification-" + key}
                    />
                  </label>
                ))}
              </div>
              <button
                type="button"
                disabled={!clarificationValues.length || Boolean(busy)}
                onClick={saveClarifications}
                className="mt-4 min-h-11 rounded-full border border-[#BFAE96] bg-[#FAF6EF] px-5 text-[13px] text-[#4A3728] disabled:opacity-45"
                data-testid="brand-clarifications-save"
              >
                {busy === "clarify" ? "Сохраняю…" : "Сохранить уточнения"}
              </button>
            </div>
          )}

          <div className="mt-5 flex flex-wrap gap-3">
            {editing ? (
              <>
                <button
                  type="button"
                  disabled={Boolean(busy)}
                  onClick={() => run("save")}
                  className="min-h-12 rounded-full bg-[#3D2A20] px-6 text-sm text-[#FFF8ED] disabled:opacity-50"
                >
                  {busy === "save" ? "Сохраняю…" : "Сохранить правки"}
                </button>
                <button
                  type="button"
                  disabled={Boolean(busy)}
                  onClick={() => {
                    setEditing(false);
                    setError("");
                  }}
                  className="min-h-12 rounded-full border border-[#D2C1AA] bg-white px-5 text-sm text-[#4A3728]"
                >
                  Отмена
                </button>
              </>
            ) : (
              <>
                {portrait.status !== "confirmed" && (
                  <button
                    type="button"
                    disabled={Boolean(busy)}
                    onClick={() => run("confirm")}
                    className="min-h-12 rounded-full bg-[#3D2A20] px-6 text-sm text-[#FFF8ED] disabled:opacity-50"
                  >
                    {busy === "confirm" ? "Подтверждаю…" : "Всё верно"}
                  </button>
                )}
                <button
                  type="button"
                  disabled={Boolean(busy)}
                  onClick={beginEdit}
                  className="inline-flex min-h-12 items-center gap-2 rounded-full border border-[#D2C1AA] bg-white px-5 text-sm text-[#4A3728]"
                >
                  <PenLine size={15} /> Хочу поправить
                </button>
              </>
            )}
          </div>

          {portrait.status === "confirmed" && !editing && (
            <div className="mt-5 border-t border-[#EEE3D5] pt-4">
              <p className="text-[12.5px] leading-5 text-[#806B55]">
                Понимание подтверждено. Следующий этап — данные бизнеса.
              </p>
              {onContinue && (
                <button
                  type="button"
                  onClick={onContinue}
                  className="mt-4 min-h-12 rounded-full bg-[#3D2A20] px-6 text-sm text-[#FFF8ED]"
                  data-testid="brand-understanding-continue"
                >
                  Продолжить к данным бизнеса
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {error && (
        <div
          className="mt-4 rounded-xl border border-[#E2CFC8] bg-[#FFF8F4] p-4 text-[13px] leading-5 text-[#6B443A]"
          role="alert"
        >
          {error}
        </div>
      )}
    </section>
  );
}
